/**
 * Checkout progress.
 *
 * One step model for the whole flow, in the order the donor actually walks it:
 *
 *     Distribution Detail → Donor Info → Recipient Info → Payment Info →
 *     Review Order
 *
 * The artwork used two incompatible models — Donor Information showed
 * "Information / Payment / Review / Confirmation" while the other screens
 * showed "Information / Order Details / …", and Thank You still highlighted
 * Review (gap G-01). Listing Information first also meant Order Details
 * rendered "Information ✓" before the donor had entered any, which is the
 * order this now corrects.
 */

import React, { memo } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, radius, space } from '../../theme';
import { CheckIcon } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export const CHECKOUT_STEPS = [
  'Distribution Detail',
  'Donor Info',
  'Recipient Info',
  'Payment Info',
  'Review Order',
] as const;
export type CheckoutStep = (typeof CHECKOUT_STEPS)[number];

/**
 * A tiffin order's steps: the plan and the box, then one short screen each
 * for persons, the contact person and delivery, then the same close as a
 * donation.
 */
export const TIFFIN_STEPS = [
  'Your Tiffin',
  'Persons',
  'Contact Info',
  'Delivery Info',
  'Payment Info',
  'Review Order',
] as const;

export interface CheckoutStepperProps {
  /** Index of the active step, or steps.length when all are done. */
  current: number;
  /** The flow's steps. A donation's five unless told otherwise. */
  steps?: readonly string[];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const CheckoutStepper = memo(function CheckoutStepper({
  current,
  steps = CHECKOUT_STEPS,
  style,
  testID = 'checkout-stepper',
}: CheckoutStepperProps) {
  const t = useT();
  return (
    <View
      style={[styles.container, style]}
      testID={testID}
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 1, max: steps.length, now: Math.min(current + 1, steps.length) }}
      accessibilityLabel={
        current >= steps.length
          ? 'All checkout steps complete'
          : `Step ${current + 1} of ${steps.length}: ${t(steps[current] ?? "")}`
      }
    >
      {steps.map((label, index) => {
        const complete = index < current;
        const active = index === current;
        return (
          <React.Fragment key={label}>
            {index > 0 ? (
              <View style={[styles.connector, index <= current && styles.connectorDone]} />
            ) : null}
            <View style={styles.step}>
              <View
                testID={`step-${index}`}
                style={[
                  styles.marker,
                  complete && styles.markerComplete,
                  active && styles.markerActive,
                ]}
              >
                {complete ? (
                  <CheckIcon size={16} color={colors.bg.brand} />
                ) : (
                  <Text
                    variant="badge"
                    tone={active ? 'brand' : 'tertiary'}
                  >
                    {index + 1}
                  </Text>
                )}
              </View>
              <Text
                variant="stepLabel"
                tone={active ? 'brand' : complete ? 'secondary' : 'tertiary'}
                align="center"
                numberOfLines={2}
                style={styles.label}
              >
                {t(label)}
              </Text>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );
});

const MARKER = 32;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: space.lg,
  },
  step: {
    alignItems: 'center',
    // Five fixed 72pt columns need 360pt and a 320pt phone has 288 to give,
    // so the fifth step used to fall off the edge. The columns give way
    // instead, and the labels take another line.
    flexBasis: 72,
    flexShrink: 1,
    minWidth: 0,
    gap: space.xs,
  },
  marker: {
    width: MARKER,
    height: MARKER,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  markerComplete: {
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.brand,
  },
  markerActive: {
    backgroundColor: colors.bg.surface,
    borderColor: colors.bg.brand,
    borderWidth: 3,
  },
  connector: {
    flex: 1,
    height: 1,
    marginTop: MARKER / 2,
    backgroundColor: colors.border.default,
  },
  connectorDone: {
    backgroundColor: colors.bg.brand,
  },
  label: {
    // Centring makes a child size to its content, so a two-word label grew
    // past its column and ran into its neighbour's. Stretched, it wraps.
    alignSelf: 'stretch',
    minHeight: 32,
  },
});
